import { useEffect, useState } from "react";

import { type DefaultValues, type FieldValues, type UseFormProps, type UseFormReturn, useForm } from "react-hook-form";

import { useModalContext } from "./modal-context";

type ModalFormProps<TFieldValues extends FieldValues, TContext, TTransformedValues = TFieldValues> = Omit<
  UseFormProps<TFieldValues, TContext, TTransformedValues>,
  "defaultValues"
> & {
  defaultValues?: DefaultValues<TFieldValues>;
};

// biome-ignore lint/suspicious/noExplicitAny: generics taken verbatim from useForm
export const useModalForm = <TFieldValues extends FieldValues = FieldValues, TContext = any, TTransformedValues = TFieldValues>(
  props?: ModalFormProps<TFieldValues, TContext, TTransformedValues>,
): UseFormReturn<TFieldValues, TContext, TTransformedValues> => {
  const { peekState, setState } = useModalContext();

  const [initialModalFormValues] = useState(() => (peekState() ?? props?.defaultValues) as DefaultValues<TFieldValues> | undefined);

  const form = useForm<TFieldValues, TContext, TTransformedValues>({
    ...(props as UseFormProps<TFieldValues, TContext, TTransformedValues>),
    defaultValues: initialModalFormValues,
  });

  useEffect(() => {
    const unsubscribe = form.subscribe({
      formState: {
        values: true,
      },
      callback: ({ values }) => {
        setState(values as DefaultValues<TFieldValues>);
      },
    });

    return () => unsubscribe();
  }, [form.subscribe, setState]);

  return form;
};
